// Admin — Installer Growth Pilot onboarding submission detail.
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Download, FileText, StickyNote, Send, History } from "lucide-react";
import { formatApiError } from "./AuthContext";
import { STEPS, PRESET_CHARGES, DECLARATIONS } from "@/pages/onboarding/schema";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function OnboardingDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [noteKind, setNoteKind] = useState("note");
  const [savingNote, setSavingNote] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const [d, l] = await Promise.all([
        axios.get(`${API}/admin/onboarding/${id}`),
        axios.get(`${API}/admin/onboarding`),
      ]);
      setDoc(d.data);
      setStatuses(l.data.statuses);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setStatusMsg("");
    try {
      await axios.patch(`${API}/admin/onboarding/${id}/status`, { status });
      await load();
      setStatusMsg(`Status updated to ${status}.`);
    } catch (err) {
      setStatusMsg(formatApiError(err));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/onboarding/${id}/notes`, { text: note.trim(), kind: noteKind });
      setNote("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const openFile = async (fileId) => {
    try {
      const r = await axios.get(`${API}/admin/onboarding/${id}/files/${fileId}/url`);
      window.open(`${API.replace(/\/api$/, "")}${r.data.url}`, "_blank", "noopener");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const exportFile = async (kind) => {
    try {
      const r = await axios.get(`${API}/admin/onboarding/${id}/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `onboarding-${doc?.reference || id}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (error && !doc) return <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!doc) {
    return <div className="flex items-center justify-center h-32 text-mid-neutral"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  }

  const data = doc.data || {};
  const s1 = data.step1 || {};
  const s2 = data.step2 || {};
  const businessName = s2.tradingName || s2.legalName || s1.tradingName || s1.legalName;
  const files = (doc.files || []).filter((f) => !f.is_deleted);

  return (
    <div data-testid="admin-onboarding-detail" className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/admin/installer-growth-pilot/onboarding" className="inline-flex items-center gap-1 text-xs font-bold text-mint hover:text-forest">
            <ArrowLeft className="h-3.5 w-3.5" /> All submissions
          </Link>
          <h1 className="mt-1 text-2xl md:text-3xl font-extrabold text-forest">
            {businessName || "Onboarding submission"}
          </h1>
          <p className="mt-1 text-sm text-mid-neutral font-mono" data-testid="onboarding-ref">{doc.reference || "Draft — not yet submitted"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => exportFile("csv")} data-testid="export-csv"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-border-neutral text-forest px-3 py-2 text-xs font-bold">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button onClick={() => exportFile("pdf")} data-testid="export-pdf"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-border-neutral text-forest px-3 py-2 text-xs font-bold">
            <Download className="h-3.5 w-3.5" /> PDF
          </button>
          <select value={doc.status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select"
            className="p-2 rounded-lg bg-forest text-white text-xs font-bold border-0">
            {[doc.status, ...statuses.filter((s) => s !== doc.status)].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </header>
      {statusMsg && <p className="text-xs font-semibold text-forest" data-testid="status-msg">{statusMsg}</p>}
      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Meta label="Submitted" value={fmt(doc.submittedAt)} />
        <Meta label="Last updated" value={fmt(doc.updatedAt)} />
        <Meta label="Applicant email" value={doc.email || "—"} />
        <Meta label="Version" value={`v${(doc.versions || []).length || "—"} (snapshot kept)`} />
      </div>

      {/* grouped answers */}
      {STEPS.filter((s) => !s.review).map((s) => (
        <Section key={s.id} title={`${s.id}. ${s.name}`}>
          <AnswerTree value={data[`step${s.id}`]} stepId={s.id} />
        </Section>
      ))}

      {/* declarations + signature */}
      <Section title="7. Review, declarations & signature">
        <ul className="space-y-1">
          {DECLARATIONS.map(([k, text]) => (
            <li key={k} className="flex gap-2 text-xs">
              <span className={(data.step7?.declarations?.[k] ? "text-mint" : "text-red-500") + " font-bold"}>
                {data.step7?.declarations?.[k] ? "✓" : "✗"}
              </span>
              <span className="text-forest">{text}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
          <Meta label="Signed by" value={data.step7?.signerName || "—"} />
          <Meta label="Title" value={data.step7?.signerTitle || "—"} />
          <Meta label="Signature" value={data.step7?.signature || "—"} />
        </div>
        {data.step7?.finalComments && <p className="mt-2 text-xs text-mid-neutral">“{data.step7.finalComments}”</p>}
      </Section>

      {/* files */}
      <Section title={`Uploaded files (${files.length})`}>
        {files.length === 0 ? (
          <p className="text-xs text-mid-neutral italic">No files uploaded.</p>
        ) : (
          <ul className="space-y-1">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-2 rounded-lg bg-soft px-3 py-2 text-xs" data-testid={`file-${f.id}`}>
                <FileText className="h-3.5 w-3.5 text-mint shrink-0" />
                <span className="font-semibold text-forest truncate">{f.name}</span>
                <span className="text-mid-neutral">{f.field} · {Math.max(1, Math.round(f.size / 1024))} KB</span>
                <button onClick={() => openFile(f.id)} data-testid={`file-open-${f.id}`}
                  className="ml-auto rounded-lg bg-forest text-white px-2.5 py-1 text-[11px] font-bold">
                  View / download
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[11px] text-mid-neutral">Files open via a secure link that expires after 5 minutes.</p>
      </Section>

      {/* internal notes */}
      <Section title="Internal notes (never visible to the applicant)">
        <form onSubmit={addNote} className="flex flex-wrap items-center gap-2 mb-3">
          <select value={noteKind} onChange={(e) => setNoteKind(e.target.value)} data-testid="note-kind"
            className="p-2 rounded-lg bg-soft text-xs font-semibold text-forest border border-transparent">
            <option value="note">Internal note</option>
            <option value="info-request">Information request</option>
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="note-input"
            className="flex-1 min-w-[200px] p-2 rounded-lg bg-soft border border-transparent focus:border-mint focus:outline-none text-xs text-forest" />
          <button type="submit" disabled={savingNote || !note.trim()} data-testid="note-add"
            className="inline-flex items-center gap-1.5 rounded-lg bg-forest text-white px-3 py-2 text-xs font-bold disabled:opacity-50">
            {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : <StickyNote className="h-3 w-3" />} Add
          </button>
        </form>
        {(doc.notes || []).length === 0 ? (
          <p className="text-xs text-mid-neutral italic">No notes yet.</p>
        ) : (
          <ul className="space-y-2">
            {[...(doc.notes || [])].reverse().map((n) => (
              <li key={n.id} className="rounded-lg bg-soft p-3 text-xs" data-testid={`note-${n.id}`}>
                <p className="text-forest">{n.text}</p>
                <p className="mt-1 text-mid-neutral">
                  {n.kind === "info-request" ? <Send className="inline h-3 w-3 mr-1" /> : null}
                  {n.by} · {fmt(n.at)}{n.kind === "info-request" ? " · information request" : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* audit trail */}
      <Section title="Audit trail">
        {(doc.audit || []).length === 0 ? (
          <p className="text-xs text-mid-neutral italic">No status changes yet.</p>
        ) : (
          <ul className="space-y-1">
            {[...(doc.audit || [])].reverse().map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-forest">
                <History className="h-3 w-3 text-mint" />
                <span><strong>{a.from}</strong> → <strong>{a.to}</strong> by {a.by} · {fmt(a.at)}{a.note ? ` — ${a.note}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-border-neutral p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-mid-neutral">{label}</p>
      <p className="mt-0.5 font-semibold text-forest break-words">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-border-neutral p-5">
      <h2 className="text-sm font-extrabold text-forest mb-3">{title}</h2>
      {children}
    </section>
  );
}

// Renders a step's answers as readable rows, with packages & charges as tables.
function AnswerTree({ value, stepId }) {
  if (!value || typeof value !== "object") return <p className="text-xs text-mid-neutral italic">No answers yet.</p>;
  const rows = [];
  flatten(value, "", rows, stepId);
  const packages = stepId === 4 ? (value.packages || []) : null;
  const charges = stepId === 5 ? (value.charges || []) : null;
  return (
    <div className="space-y-4">
      {packages && packages.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="packages-table">
            <thead>
              <tr className="text-left font-bold uppercase tracking-wider text-mid-neutral border-b border-border-neutral">
                <th className="p-2">Package</th><th className="p-2">Category</th><th className="p-2">Status</th>
                <th className="p-2">Landing</th><th className="p-2">Retail</th><th className="p-2">Size</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p, i) => (
                <tr key={i} className="border-b border-border-neutral last:border-0">
                  <td className="p-2 font-semibold text-forest">{p.customerName || p.internalName || `Package ${i + 1}`}</td>
                  <td className="p-2 text-mid-neutral">{p.category || "—"}</td>
                  <td className="p-2">{p.status || "—"}</td>
                  <td className="p-2">{p.landingPrice ? `$${p.landingPrice} (${p.landingGst || "?"})` : "—"}</td>
                  <td className="p-2">{p.retailPrice ? `$${p.retailPrice} (${p.retailGst || "?"})` : "—"}</td>
                  <td className="p-2 text-mid-neutral">{[p.sysSizeKw && `${p.sysSizeKw} kW`, p.battUsableKwh && `${p.battUsableKwh} kWh`].filter(Boolean).join(" + ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {charges && charges.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="charges-table">
            <thead>
              <tr className="text-left font-bold uppercase tracking-wider text-mid-neutral border-b border-border-neutral">
                <th className="p-2">Charge</th><th className="p-2">Status</th><th className="p-2">Price</th><th className="p-2">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {PRESET_CHARGES.map(([key, name]) => ({ name, ...(charges.find((c) => c.key === key) || {}) }))
                .concat(charges.filter((c) => !c.key).map((c) => ({ ...c })))
                .map((c, i) => (
                  <tr key={i} className="border-b border-border-neutral last:border-0">
                    <td className="p-2 font-semibold text-forest">{c.name}</td>
                    <td className="p-2">{c.status || "—"}</td>
                    <td className="p-2">{c.price ? `$${c.price}${c.unit && c.status === "Unit rate" ? ` ${c.unit}` : ""} (${c.gst || "?"})` : "—"}</td>
                    <td className="p-2 text-mid-neutral">{c.trigger || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {rows.filter(([, v]) => v !== "").slice(0, 200).map(([k, v]) => (
          <div key={k} className="flex gap-2 text-xs py-0.5 border-b border-soft last:border-0">
            <dt className="text-mid-neutral w-2/5 shrink-0 break-words">{prettify(k)}</dt>
            <dd className="text-forest font-semibold break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const ACRONYMS = new Set(["abn", "acn", "url", "gst", "cms", "ga4", "gtm", "capi", "vpp", "stc", "stcs", "id", "kw", "kwh", "dnsp", "ct", "sms"]);

function prettify(key) {
  const words = key
    .replace(/^(declarations|packages|charges|licences|regions|finance|testimonials)\./, "")
    .replace(/\[\d+\]/g, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[._]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}

function flatten(value, prefix, rows, stepId) {
  if (value === null || value === undefined || value === "") return;
  if (typeof value === "boolean") { rows.push([prefix, value ? "Yes" : "No"]); return; }
  if (Array.isArray(value)) {
    if (!value.length) return;
    if (prefix === "packages" || prefix === "charges") return; // rendered as tables above
    value.forEach((item, i) => {
      if (typeof item === "object") flatten(item, `${prefix}[${i + 1}]`, rows, stepId);
      else rows.push([`${prefix}[${i + 1}]`, String(item)]);
    });
    return;
  }
  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value)) flatten(v, prefix ? `${prefix}.${k}` : k, rows, stepId);
    return;
  }
  rows.push([prefix, String(value)]);
}
